import dayjs from 'dayjs';
import type { WeatherGrid, WindField } from '../types/weatherGrid';

/** "7×5 grid" */
export const weatherGridShapeLabel = (grid: Pick<WeatherGrid, 'shape'>): string =>
  `${grid.shape[0]}×${grid.shape[1]} grid`;

/** Suffix appended to a forecast weather label when a grid rides along. */
export const weatherGridLabelSuffix = (grid: Pick<WeatherGrid, 'shape'> | null | undefined): string =>
  grid ? ` · ${weatherGridShapeLabel(grid)}` : '';

const GRID_SUFFIX_RE = /\s·\s(\d+)×(\d+)\sgrid$/;

/** Recover the [rows, cols] shape from a label carrying the grid suffix. */
export const parseWeatherGridSuffix = (label: string | null | undefined): [number, number] | null => {
  if (!label) return null;
  const m = GRID_SUFFIX_RE.exec(label);
  return m ? [Number(m[1]), Number(m[2])] : null;
};

/** Drop the grid suffix (used when a run is replayed without its grid). */
export const stripWeatherGridSuffix = (label: string): string => label.replace(GRID_SUFFIX_RE, '');

/**
 * True for labels produced by the forecast (Run Date) view — "Jan 15, 2026
 * weather", optionally followed by the grid suffix. Custom weather is
 * "Custom weather" and is excluded.
 */
export const isForecastWeatherLabel = (label: string): boolean => {
  const base = stripWeatherGridSuffix(label);
  return base.endsWith(' weather') && !base.startsWith('Custom');
};

/** Number of lattice points. */
export const weatherGridPointCount = (grid: Pick<WeatherGrid, 'shape'>): number =>
  grid.shape[0] * grid.shape[1];

/**
 * Index into `grid.timestamps` for the hour containing `when`, or -1. Matches
 * on the hour (same rule the animation uses for the scalar series).
 */
export const findWeatherGridTimestampIndex = (grid: WeatherGrid, when: dayjs.Dayjs): number => {
  for (let t = 0; t < grid.timestamps.length; t++) {
    if (dayjs(grid.timestamps[t]).isSame(when, 'hour')) return t;
  }
  return -1;
};

/** The wind field for one timestamp index of the grid (null if out of range). */
export const windFieldFromWeatherGrid = (grid: WeatherGrid, t: number): WindField | null => {
  const speed = grid.vars?.wind_speed?.[t];
  const direction = grid.vars?.wind_direction?.[t];
  if (!speed || !direction) return null;
  const n = grid.lats.length * grid.lons.length;
  if (speed.length !== n || direction.length !== n) return null;
  return { lats: grid.lats, lons: grid.lons, speed, direction };
};

/**
 * Bracket `x` in a monotonic (ascending or descending) axis. Returns the two
 * neighbouring indices and the fraction towards the second; clamps outside
 * the axis to the edge sample so the field never extrapolates.
 */
const bracket = (axis: number[], x: number): [number, number, number] => {
  const n = axis.length;
  if (n === 1) return [0, 0, 0];
  const ascending = axis[n - 1] >= axis[0];
  const lo = ascending ? axis[0] : axis[n - 1];
  const hi = ascending ? axis[n - 1] : axis[0];
  if (x <= lo) return ascending ? [0, 0, 0] : [n - 1, n - 1, 0];
  if (x >= hi) return ascending ? [n - 1, n - 1, 0] : [0, 0, 0];
  for (let i = 0; i < n - 1; i++) {
    const a = axis[i];
    const b = axis[i + 1];
    const inside = ascending ? x >= a && x <= b : x <= a && x >= b;
    if (inside) {
      const span = b - a;
      const f = span === 0 ? 0 : (x - a) / span;
      return [i, i + 1, f];
    }
  }
  return [n - 1, n - 1, 0];
};

const DEG = Math.PI / 180;

/**
 * Bilinear sample of the wind field at (lng, lat). Speed is interpolated
 * directly; direction is interpolated via unit vectors (weighted sin/cos,
 * then atan2) so 350° and 10° average to 0°, not 180°.
 */
export const sampleWindField = (field: WindField, lng: number, lat: number): { speed: number; direction: number } => {
  const cols = field.lons.length;
  const [i0, i1, fi] = bracket(field.lats, lat);
  const [j0, j1, fj] = bracket(field.lons, lng);

  const w00 = (1 - fi) * (1 - fj);
  const w01 = (1 - fi) * fj;
  const w10 = fi * (1 - fj);
  const w11 = fi * fj;
  const k00 = i0 * cols + j0;
  const k01 = i0 * cols + j1;
  const k10 = i1 * cols + j0;
  const k11 = i1 * cols + j1;

  const speed =
    w00 * field.speed[k00] + w01 * field.speed[k01] + w10 * field.speed[k10] + w11 * field.speed[k11];

  const d00 = field.direction[k00] * DEG;
  const d01 = field.direction[k01] * DEG;
  const d10 = field.direction[k10] * DEG;
  const d11 = field.direction[k11] * DEG;
  const u = w00 * Math.sin(d00) + w01 * Math.sin(d01) + w10 * Math.sin(d10) + w11 * Math.sin(d11);
  const v = w00 * Math.cos(d00) + w01 * Math.cos(d01) + w10 * Math.cos(d10) + w11 * Math.cos(d11);
  let direction = Math.atan2(u, v) / DEG;
  if (direction < 0) direction += 360;
  if (direction >= 360) direction -= 360;

  return { speed: Number.isFinite(speed) ? speed : 0, direction: Number.isFinite(direction) ? direction : 0 };
};
